import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  CartItemState,
  So5LeaderboardSeasonality,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, LabelM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { SportSeasonalityIcon } from '@sorare/core/src/components/SportSeasonalityIcon';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { SorareUser } from '@sorare/core/src/components/user/SorareUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { cart, glossary } from '@sorare/core/src/lib/glossary';
import { anyPositionShortNames } from '@sorare/core/src/lib/players';
import {
  ClickRemoveFromCart_Origin,
  ClickSaveForLater_Origin,
} from '@sorare/core/src/protos/events/so5/shared/events';

import { CardBonus } from '#src/components/card/CardBonus/index.tsx';
import { CollectionPrediction } from '#src/components/card/CollectionPrediction/index.tsx';
import { CartAction } from '#src/components/cart/CartAction/index.tsx';
import { CartItemStateWarning } from '#src/components/cart/CartItemStateWarning/index.tsx';
import { PromoBanners } from '#src/components/market/PromoBanners/index.tsx';
import { FlexCard } from '#src/components/token/FlexCard/index.tsx';
import { useCartEvents } from '#src/hooks/events/useCartEvents.ts';
import { useCart } from '#src/hooks/useCart.ts';

import type {
  CartItem_anyCard,
  CartItem_cartItem,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 1 })`
  width: 100%;
  .clear-cart &,
  &.loading {
    filter: blur(10px);
  }
`;

const Metas = styled(Vertical)`
  flex-grow: 1;
  align-items: flex-start;
`;

const CardWrapper = styled.div`
  width: 80px;
  margin-right: var(--half-unit);
  .removed & {
    filter: grayscale(1);
  }
`;
const StyledLabelM = styled(LabelM)`
  > * {
    vertical-align: baseline;
  }
`;

type Props = {
  cartItem: Nullable<CartItem_cartItem>;
  card: Nullable<CartItem_anyCard>;
  withPromoBanner?: boolean;
  onAction?: () => void;
  origin?: {
    remove: ClickRemoveFromCart_Origin;
    saveForLater: ClickSaveForLater_Origin;
  };
};

export const CartItem = ({
  cartItem,
  card,
  withPromoBanner = false,
  onAction,
  origin = {
    remove: ClickRemoveFromCart_Origin.UNRECOGNIZED,
    saveForLater: ClickSaveForLater_Origin.UNRECOGNIZED,
  },
}: Props) => {
  const { formatMessage } = useIntlContext();
  const { toMonetaryAmount } = useMonetaryAmount();

  const { trackClickRemoveFromCart, trackClickSaveForLater } = useCartEvents({
    anyCard: card!,
  });

  const { removeFromCart, saveForLater } = useCart();

  if (!cartItem || !card) return null;

  const price = toMonetaryAmount(cartItem.price);
  const removed = cartItem.state === CartItemState.REMOVED;

  return (
    <Root
      className={classNames({
        removed,
        loading: removeFromCart.loading || saveForLater.loading,
      })}
    >
      <CartItemStateWarning
        cartItem={cartItem}
        trackRemove={() => trackClickRemoveFromCart(origin.remove)}
      />
      <Horizontal>
        <CardWrapper>
          <FlexCard card={card} width={80} />
        </CardWrapper>
        <Metas>
          {!!removed && (
            <Chip size="small">
              <FormattedMessage {...cart.soldOut} />
            </Chip>
          )}
          <Vertical gap={0.5}>
            <BodyL bold>{card?.anyPlayer?.displayName}</BodyL>
            <StyledLabelM color="var(--c-nd-600)">
              {card.anyPositions
                .map(position => formatMessage(anyPositionShortNames[position]))
                .join(',')}
              {' • '}
              <SportSeasonalityIcon
                size={1.5}
                seasonality={So5LeaderboardSeasonality.IN_SEASON}
              />
              {' • '}
              {card.serialNumber}/{card?.supply}
              {' • '}
              <SorareUser />
            </StyledLabelM>
          </Vertical>
          {!removed && (
            <>
              <Horizontal>
                <CardBonus card={card} />
                <CollectionPrediction card={card} />
              </Horizontal>
              <Horizontal>
                <CartAction
                  onClick={() => {
                    onAction?.();
                    trackClickRemoveFromCart(origin.remove);
                    removeFromCart.mutate([cartItem.id], card.sport);
                  }}
                >
                  <FormattedMessage {...glossary.remove} />
                </CartAction>
                <CartAction
                  onClick={() => {
                    onAction?.();
                    trackClickSaveForLater(origin.saveForLater);
                    saveForLater.mutate([cartItem.id], card.sport);
                  }}
                >
                  <FormattedMessage {...cart.saveForLater} />
                </CartAction>
              </Horizontal>
            </>
          )}
        </Metas>
        {!price.isZero && (
          <span className="text-right">
            <AmountWithConversion column monetaryAmount={price} />
          </span>
        )}
      </Horizontal>
      {!!withPromoBanner && !!card?.livePrimaryOffer && (
        <PromoBanners promotionalEvents={card.livePrimaryOffer} rounded />
      )}
    </Root>
  );
};

CartItem.fragments = {
  anyCard: gql`
    fragment CartItem_anyCard on AnyCardInterface {
      slug
      serialNumber
      supply
      anyPlayer {
        slug
        displayName
      }
      anyPositions
      livePrimaryOffer {
        id
        ...PromoBanners_PromotionalEventsInterface
      }
      ...FlexCard_anyCard
      ...CardBonus_anyCard
      ...CollectionPrediction_anyCard
      ...useCartEvents_anyCard
    }
    ${FlexCard.fragments.anyCard}
    ${CardBonus.fragments.anyCard}
    ${CollectionPrediction.fragments.anyCard}
    ${useCartEvents.fragments.anyCard}
    ${PromoBanners.fragments.PromotionalEventsInterface}
  ` as TypedDocumentNode<CartItem_anyCard>,
  cartItem: gql`
    fragment CartItem_cartItem on CartItem {
      id
      state
      price {
        ...useMonetaryAmount_monetaryAmount
      }
      ...CartItemStateWarning_cartItem
    }
    ${CartItemStateWarning.fragments.cartItem}
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<CartItem_cartItem>,
};
